import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { debounce, isUndefined } from 'lodash'
import { Tooltip } from 'antd'

import ResizingDiv from './ResizingDiv'
import { ColorsPalette } from '../../../../config/colors'
import { colorShade, hexToRgb } from '../../../../utils/colors'
import VerticalDragLine from './VerticalDragLine'
import { valueFromCss } from '../../../../utils/cssHelper'
import { Events } from '../../../../utils/events'
import { HighlightedColumnsContext } from '../../../../Providers/Quotas/HighlightedColumnsProvider'
import { isCtrlKeyPressed } from '../../../../utils'

const TABLE_STYLE = { maxHeight: '100%', width: '100%', position: 'relative' }
const OVERFLOW_COLOR = { backgroundColor: colorShade(ColorsPalette.FIRE_BRICK, 110), color: ColorsPalette.FIRE_BRICK }

const SwimLane = ({
  cellConf, data, height, firstRowColor, highlightedColumns, infos, shouldDisplayOverflow, overflow, overlay, width, seed, selectedCell, selectedData, title, titleCell, tooltip, zIndex,
  onEvent, onIllegalDrag
}) => {
  const hcContext = useContext(HighlightedColumnsContext)

  const [mode, setMode] = useState(null)
  const [area, setArea] = useState({ start: null, end: null })
  const [localOverflow, setLocalOverflow] = useState(0)

  const overflowInterval = useRef(null)

  useEffect(() => {
    setLocalOverflow(overflow)
  }, [overflow])

  const tableRef = useRef(null)
  const longPressRef = useRef(null)

  const localHeight = useMemo(() => {
    const displayedHeight = shouldDisplayOverflow ? height : height + localOverflow

    return displayedHeight > 1 ? displayedHeight : 1
  }, [height, localOverflow, shouldDisplayOverflow])

  const displayedOverflow = useMemo(() => shouldDisplayOverflow ? localOverflow : 0, [localOverflow])
  const gridColor = overlay.displayGrid ? ColorsPalette.SILVER : ColorsPalette.TRANSPARENT
  const borderStyle = useCallback(color => `2px solid ${color}`, [])
  const boundaries = useMemo(() => ({ x: [0, width - 1], y: [0, localHeight + displayedOverflow - 1] }), [localHeight, width, displayedOverflow])
  const divConfig = useMemo(() => ({
    boundaries,
    displacement: { x: Math.max(valueFromCss(cellConf.style.minWidth), valueFromCss(cellConf.style.width)), y: valueFromCss(cellConf.style.height) },
    overlay,
    keyboard: cellConf.keyboard
  }), [boundaries, cellConf.style, overlay])
  const titleCellStyle = useMemo(() => ({
    ...titleCell.style,
    border: titleCell.style.border ?? borderStyle(gridColor),
    position: 'relative'
  }), [borderStyle(gridColor), titleCell.style])

  const getHighlight = ({ x, y }) => {
    const style = { className: 'standard-cell' }
    const highlightBorderStyle = borderStyle(ColorsPalette.LIGHT_BLACK)

    if (area.start !== null && area.end !== null && mode === Events.LONG_CLICK) {
      if (area.start.x <= x && x <= area.end.x && area.start.y <= y && y <= area.end.y) {
        style.backgroundColor = 'rgba(0, 0, 0, 0.1)'

        if (x === area.start.x) {
          style.borderLeft = highlightBorderStyle
        }

        if (x === area.end.x) {
          style.borderRight = highlightBorderStyle
        }

        if (y === area.start.y) {
          style.borderTop = highlightBorderStyle
        }

        if (y === area.end.y) {
          style.borderBottom = highlightBorderStyle
        }
      } else if (x === area.start.x - 1 && y <= area.end.y && y >= area.start.y) {
        style.borderRight = highlightBorderStyle
      } else if (y === area.start.y - 1 && x <= area.end.x && x >= area.start.x) {
        style.borderBottom = highlightBorderStyle
      }
    } else if (selectedCell !== null && x === selectedCell.x[0] && y === selectedCell.y[0]) {
      style.className = 'selected-cell'
    }

    if (!isUndefined(hcContext) && hcContext.area.init !== null && hcContext.area.start <= x && x <= hcContext.area.end) {
      style.backgroundColor = '#cde9ff'
    } else if (highlightedColumns.includes(x)) {
      style.backgroundColor = ColorsPalette.LIGHT_GREY
    } else if (y >= localHeight) {
      style.className = style.className !== 'selected-cell' ? 'overflow-cell' : 'selected-cell'
      style.backgroundColor = hexToRgb(OVERFLOW_COLOR.backgroundColor, 0.65)
    }

    return style
  }

  const handleMouseDown = e => {
    e.preventDefault()
    e.stopPropagation()

    if (mode === Events.LONG_CLICK) {
      setMode(null)

      onEvent(Events.LONG_CLICK, { x: [area.start.x, area.end.x], y: [area.start.y, area.end.y], data: null })
    } else {
      const startCell = getCoordinates({ x: e.clientX, y: e.clientY })

      setArea({ start: startCell, end: startCell })

      longPressRef.current = debounce(() => setMode(Events.LONG_CLICK), 500)
      longPressRef.current()
    }
  }

  const handleMouseUp = e => {
    const startCell = area.start
    const endCell = area.end

    if (mode === Events.LONG_CLICK) {
      setMode(null)

      onEvent(Events.LONG_CLICK, { x: [startCell.x, endCell.x], y: [startCell.y, endCell.y], data: null })
    } else if (mode === Events.CLICK_COLDOWN) {
      setMode(null)
    } else if (startCell) {
      longPressRef.current.cancel()

      onEvent(Events.CLICK, { x: [startCell.x, startCell.x], y: [startCell.y, startCell.y], data: null })
    }

    setArea({ start: null, end: null })
  }

  const getCoordinates = ({ x, y }) => {
    const table = tableRef.current.getBoundingClientRect()

    return {
      x: Math.floor((x - table.left) / divConfig.displacement.x),
      y: Math.floor((y - table.top) / divConfig.displacement.y)
    }
  }

  const handleDragStop = useCallback(data => {
    setLocalOverflow(overflow)
    onEvent(Events.DRAG_STOP, data)
  }, [onEvent, overflow])

  const handleCellDoubleClick = cell => onEvent(Events.DOUBLE_CLICK, cell)

  const handleDataClick = data => event => onEvent(isCtrlKeyPressed(event) ? Events.CONTROL_CLICK : Events.CLICK, data)

  const handleDataDoubleClick = data => onEvent(Events.DOUBLE_CLICK, data)

  const logMouseMovement = e => {
    if (mode === Events.LONG_CLICK && area.start !== null) {
      const endCell = getCoordinates({ x: e.clientX, y: e.clientY })

      if (area.end === null || endCell.x !== area.end.x || endCell.y !== area.end.y) {
        setArea({ ...area, end: endCell })
      }
    }
  }

  const lines = useMemo(() => {
    const lines = []
    let cells = []

    const localWidth = width <= 1 ? 1 : width

    for (let y = 0; y < (localHeight + displayedOverflow); y++) {
      for (let x = 0; x < localWidth; x++) {
        const localStyle = getHighlight({ x, y })

        if (firstRowColor && y === 0) {
          localStyle.backgroundColor = firstRowColor
        }

        cells.push(<td key={`cell_${y}_${x}`} className={localStyle.className} style={{ ...cellConf.style, ...localStyle }} onDoubleClick={() => handleCellDoubleClick({ x: [x, x], y: [y, y] })} />)
      }

      lines.push((
        <tr key={`line_${y}`}>
          {cells}
        </tr>
      ))
      cells = []
    }

    return lines
  }, [height, title, width, displayedOverflow, titleCellStyle, firstRowColor, getHighlight, handleCellDoubleClick])

  const handleMouseLeave = useCallback(e => {
    if (mode === Events.LONG_CLICK) {
      const table = tableRef.current.getBoundingClientRect()

      if (e.clientY >= table.top) {
        overflowInterval.current = setInterval(() => setLocalOverflow(o => o + 1), 50)
      }
    }
  }, [tableRef.current, mode])

  const handleMouseEnter = useCallback((e) => {
    if (overflowInterval.current) {
      clearInterval(overflowInterval.current)
      overflowInterval.current = null

      const endCell = getCoordinates({ x: e.clientX, y: e.clientY })

      setArea(area => ({ ...area, end: endCell }))
    }
  }, [])

  return (
    <div className='swim-lane-container' style={{ ...TABLE_STYLE, zIndex }} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter}>
      <div className='flex-row w-mc'>
        <div className='flex-column sticky'>
          <VerticalDragLine
            displacement={divConfig.displacement.y}
            offset={localHeight + displayedOverflow}
            min={height + overflow}
            onChange={offset => setLocalOverflow(offset - (height <= 1 ? 1 : height))}
          />
          <div className='drag-table-title' style={{ ...titleCellStyle, height: (localHeight * divConfig.displacement.y) + 1 }}>
            {infos.length > 0 && (
              <div style={{ position: 'absolute', top: 0, right: 0, margin: '2px 5px' }}>
                {infos}
              </div>
            )}
            <Tooltip title={tooltip} placement='top'>
              <div className='flex-row jc-ctr ai-ctr w-100 h-100'> {title} </div>
            </Tooltip>
          </div>
          {displayedOverflow > 0 && (
            <div
              className='drag-table-title flex-row jc-ctr ai-ctr'
              style={{ ...titleCellStyle, ...OVERFLOW_COLOR, height: displayedOverflow * divConfig.displacement.y }}
            >
              {displayedOverflow}
            </div>
          )}
        </div>
        <div className='relative'>
          <table ref={tableRef} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} onMouseMove={logMouseMovement}>
            <tbody>
              {lines}
            </tbody>
          </table>
          {data.map((d, index) => (
            <ResizingDiv
              key={`key_${index}`}
              config={divConfig}
              content={d.value}
              data={{ ...d }}
              type={d.type}
              isSelected={selectedData.find(selected => selected.id === d.id)}
              onClick={handleDataClick(d)}
              onDoubleClick={handleDataDoubleClick}
              onDragStop={handleDragStop}
              onMaxIndexOverflow={() => setLocalOverflow(localOverflow + 1)}
              onIllegalDrag={onIllegalDrag}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default SwimLane
